import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useRef } from "react";
import { z } from "zod";
import type { EventOutsourceDTO } from "../../domain/contracts";
import {
  useOutsourceCategoriesQuery,
  useOutsourcesListQuery,
  useStaffQuery,
} from "../api/queries";
import {
  AD_HOC_OUTSOURCE_VALUE,
  OutsourceBankFields,
  OutsourceCategoryTypeSection,
  OutsourceContactFields,
  OutsourceEventDealFields,
  OutsourceFormPreview,
  OutsourceIdentityFields,
  OutsourceMasterPickField,
  OutsourceStaffPickField,
  applyStaffToOutsourceForm,
  fromMasterSelectValue,
  toMasterSelectValue,
} from "./outsource-form-ui";

const EventOutsourceFormSchema = z
  .object({
    staffPickId: z.string().optional(),
    outsourceId: z.string().optional(),
    categoryId: z.string().min(1, "ເລືອກຫມວດ"),
    payeeType: z.enum(["individual", "team"]),
    displayName: z.string().trim().min(1, "ຕ້ອງປ້ອນຊື່"),
    contactName: z.string().optional(),
    roleLabel: z.string().optional(),
    deliverableSummary: z.string().optional(),
    agreedAmount: z.number().int().positive("ຍອດຕ້ອງຫຼາຍກວ່າ 0"),
    phone: z.string().optional(),
    email: z.string().optional(),
    bankName: z.string().optional(),
    bankAccount: z.string().optional(),
    bankAccountName: z.string().optional(),
  })
  .superRefine((vals, ctx) => {
    if (vals.payeeType === "individual" && vals.contactName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactName"],
        message: "ບຸກຄົນ — ບໍ່ຕ້ອງປ້ອນຜູ້ຕິດຕໍ່",
      });
    }
  });

type EventOutsourceFormValues = z.infer<typeof EventOutsourceFormSchema>;

type EventOutsourceFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: EventOutsourceDTO | null;
  onSubmit: (values: EventOutsourceFormValues) => void;
  submitting?: boolean;
};

export function EventOutsourceFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
  submitting,
}: EventOutsourceFormDialogProps) {
  const categoriesQuery = useOutsourceCategoriesQuery(open);
  const mastersQuery = useOutsourcesListQuery({
    q: undefined,
    limit: 100,
    skip: 0,
  });

  const categories = categoriesQuery.data?.data ?? [];
  const prevStaffPickIdRef = useRef<string | null>(null);

  const methods = RHF.useForm<EventOutsourceFormValues>({
    resolver: zodResolver(EventOutsourceFormSchema),
    defaultValues: {
      staffPickId: "",
      outsourceId: AD_HOC_OUTSOURCE_VALUE,
      categoryId: "",
      payeeType: "individual",
      displayName: "",
      contactName: "",
      roleLabel: "",
      deliverableSummary: "",
      agreedAmount: 0,
      phone: "",
      email: "",
      bankName: "",
      bankAccount: "",
      bankAccountName: "",
    },
  });

  const payeeType = methods.watch("payeeType");
  const categoryId = methods.watch("categoryId");
  const selectedMasterId = methods.watch("outsourceId");
  const staffPickId = methods.watch("staffPickId");
  const displayName = methods.watch("displayName");
  const agreedAmount = methods.watch("agreedAmount");
  const selectedCategory = categories.find((c) => c.id === categoryId);
  const { data: staffDetail } = useStaffQuery(staffPickId ?? "");

  useEffect(() => {
    if (!open) return;
    prevStaffPickIdRef.current = null;
    methods.reset({
      staffPickId: "",
      outsourceId: toMasterSelectValue(editing?.outsourceId),
      categoryId: editing?.categoryId ?? "",
      payeeType: editing?.payeeType ?? "individual",
      displayName: editing?.displayName ?? "",
      contactName: editing?.contactName ?? "",
      roleLabel: editing?.roleLabel ?? "",
      deliverableSummary: editing?.deliverableSummary ?? "",
      agreedAmount: editing?.agreedAmount ?? 0,
      phone: editing?.phone ?? "",
      email: editing?.email ?? "",
      bankName: editing?.bankName ?? "",
      bankAccount: editing?.bankAccount ?? "",
      bankAccountName: editing?.bankAccountName ?? "",
    });
  }, [open, editing, methods]);

  useEffect(() => {
    if (!open || editing) return;
    if (selectedCategory?.defaultPayeeType) {
      methods.setValue("payeeType", selectedCategory.defaultPayeeType);
    }
  }, [selectedCategory?.defaultPayeeType, editing, methods, open]);

  useEffect(() => {
    if (
      !open ||
      editing ||
      !selectedMasterId ||
      selectedMasterId === AD_HOC_OUTSOURCE_VALUE
    ) {
      return;
    }
    const master = mastersQuery.data?.data.find((m) => m.id === selectedMasterId);
    if (!master) return;
    methods.setValue("staffPickId", "");
    prevStaffPickIdRef.current = null;
    methods.setValue("categoryId", master.categoryId);
    methods.setValue("payeeType", master.payeeType);
    methods.setValue("displayName", master.displayName);
    methods.setValue("contactName", master.contactName ?? "");
    methods.setValue("phone", master.phone ?? "");
    methods.setValue("email", master.email ?? "");
    methods.setValue("bankName", master.bankName ?? "");
    methods.setValue("bankAccount", master.bankAccount ?? "");
    methods.setValue("bankAccountName", master.bankAccountName ?? "");
  }, [selectedMasterId, mastersQuery.data, editing, methods, open]);

  useEffect(() => {
    if (!open || editing || !staffPickId || !staffDetail) return;
    if (staffPickId === prevStaffPickIdRef.current) return;
    prevStaffPickIdRef.current = staffPickId;
    methods.setValue("outsourceId", AD_HOC_OUTSOURCE_VALUE);
    applyStaffToOutsourceForm(methods.setValue, staffDetail, "event");
  }, [open, editing, staffPickId, staffDetail, methods]);

  const masterOptions =
    mastersQuery.data?.data
      .filter((m) => m.isActive)
      .map((m) => ({ value: m.id, label: m.displayName })) ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] max-w-2xl overflow-x-hidden overflow-y-auto sm:w-full">
        <DialogHeader>
          <DialogTitle>
            {editing ? "ແກ້ໄຂ Outsource" : "ເພີ່ມ Outsource"}
          </DialogTitle>
          <DialogDescription>
            AP ຕົ້ນທຸນ KOL/Media — ລູກຄ້າເຫັນແຕ່ລາຄາໃນ QT/INV · Agent ແຍກຢູ່ຄ່ານາຍໜ້າ
          </DialogDescription>
        </DialogHeader>

        <FormRoot
          methods={methods}
          onSubmit={(vals) => {
            onSubmit({
              ...vals,
              outsourceId: fromMasterSelectValue(vals.outsourceId),
              contactName:
                vals.payeeType === "team" ? vals.contactName?.trim() || null : null,
            });
          }}
          className="min-w-0 w-full max-w-full space-y-5"
        >
          <OutsourceFormPreview
            mode="event"
            displayName={displayName}
            payeeType={payeeType}
            categoryName={selectedCategory?.nameLo}
            categorySlug={selectedCategory?.slug}
            agreedAmount={agreedAmount}
          />

          {!editing ? (
            <>
              <OutsourceStaffPickField name="staffPickId" />
              <OutsourceMasterPickField
                name="outsourceId"
                masterOptions={masterOptions}
              />
            </>
          ) : null}

          <OutsourceCategoryTypeSection categories={categories} />
          <OutsourceIdentityFields payeeType={payeeType} />
          <OutsourceEventDealFields />
          <OutsourceContactFields />
          <OutsourceBankFields />

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting
                ? "ກຳລັງບັນທຶກ..."
                : editing
                  ? "ບັນທຶກ"
                  : "ເພີ່ມ Outsource"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
